import React, { useState } from 'react';
import { Upload, X, Plus, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { adminUpload, asset } from '@/lib/api';
import { toast } from 'sonner';

const ImageUpload = ({ value, onChange }) => {
  const [loading, setLoading] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLoading(true);
    try {
      const res = await adminUpload(f);
      onChange(res.url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className='flex items-center gap-3'>
      {value ? (
        <img src={asset(value)} alt='' className='h-16 w-24 rounded-lg border border-[#20272A]/10 object-cover' />
      ) : (
        <div className='flex h-16 w-24 items-center justify-center rounded-lg border border-dashed border-[#20272A]/25 text-[#20272A]/30'>
          <Upload size={18} />
        </div>
      )}
      <div className='flex flex-col gap-1'>
        <label className='inline-flex cursor-pointer items-center gap-2 rounded-lg bg-[#075B61] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#0a6d74]'>
          {loading ? <Loader2 size={13} className='animate-spin' /> : <Upload size={13} />} Upload
          <input type='file' className='hidden' accept='.jpg,.jpeg,.png,.webp,.gif' onChange={upload} />
        </label>
        <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder='or paste URL / path' className='h-8 w-64 text-xs' />
      </div>
    </div>
  );
};

const ImageList = ({ value = [], onChange }) => {
  const list = Array.isArray(value) ? value : [];
  const add = (url) => onChange([...list, url]);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className='flex flex-wrap gap-2'>
        {list.map((img, i) => (
          <div key={i} className='relative'>
            <img src={asset(img)} alt='' className='h-16 w-24 rounded-lg border border-[#20272A]/10 object-cover' />
            <button type='button' onClick={() => remove(i)} className='absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-[#20272A] text-white'><X size={11} /></button>
          </div>
        ))}
      </div>
      <div className='mt-2'>
        <ImageUpload value='' onChange={(url) => url && add(url)} />
      </div>
    </div>
  );
};

export const AdminField = ({ field, value, onChange }) => {
  const { name, label, type, options } = field;
  return (
    <div className={type === 'textarea' || type === 'imagelist' ? 'sm:col-span-2' : ''}>
      <Label className='text-xs font-medium text-[#20272A]/70'>{label}</Label>
      <div className='mt-1.5'>
        {type === 'text' && <Input value={value ?? ''} onChange={(e) => onChange(e.target.value)} className='h-10' />}
        {type === 'number' && <Input type='number' value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))} className='h-10' />}
        {type === 'textarea' && <Textarea value={value ?? ''} onChange={(e) => onChange(e.target.value)} className='min-h-[90px]' />}
        {type === 'bool' && (
          <div className='flex items-center gap-2'>
            <Switch checked={!!value} onCheckedChange={onChange} /> <span className='text-sm text-[#20272A]/60'>{value ? 'Yes' : 'No'}</span>
          </div>
        )}
        {type === 'select' && (
          <Select value={value || ''} onValueChange={onChange}>
            <SelectTrigger className='h-10'><SelectValue placeholder='Select' /></SelectTrigger>
            <SelectContent>
              {(options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {type === 'list' && (
          <Textarea
            value={Array.isArray(value) ? value.join('\n') : (value || '')}
            onChange={(e) => onChange(e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))}
            className='min-h-[80px]'
            placeholder='One item per line'
          />
        )}
        {type === 'image' && <ImageUpload value={value} onChange={onChange} />}
        {type === 'imagelist' && <ImageList value={value} onChange={onChange} />}
      </div>
    </div>
  );
};
